/**
 * [Phase 2c-B §4.2] RoundProgressBar - visual "X / Y rounds done" indicator.
 *
 * [HF2-4 v1.1] Formula switched from "rounds completed before current"
 * (((cr - 1) / tr) * 100) to global pick progress
 * (total_picks_made / total_picks * 100). This:
 *   • Reads as a smooth global advance through the draft, not 14 stair-step
 *     jumps at round boundaries
 *   • Naturally clamps to 100% when DRAFT_COMPLETE - the previous formula
 *     never reached 100% because the snake engine leaves current_round=14
 *     after the final pick (snake_draft.py:159-163), so cr=14, pct=92.86%
 *     and the bar visually sat at 13/14 even when 14/14 picks were complete.
 *
 * Now accepts `draftState` directly so all relevant counters (current_round,
 * total_picks_made, roster_size, draft_order.length, state) come from one
 * source. Legacy `currentRound`/`totalRounds` props still accepted as a
 * fallback for older callers.
 */
import React from "react";

export default function RoundProgressBar({ draftState, currentRound, totalRounds, isDark }) {
  // Prefer draftState; fall back to legacy props
  const cr = draftState?.current_round ?? currentRound ?? 1;
  const tr = draftState?.roster_size ?? totalRounds ?? 14;
  const teamCount = draftState?.draft_order?.length || 12;
  const totalPicks = teamCount * tr;
  const made = draftState?.total_picks_made || 0;
  const isComplete = draftState?.state === "DRAFT_COMPLETE";

  const pct = isComplete
    ? 100
    : (totalPicks > 0 ? Math.min(100, Math.round((made / totalPicks) * 100)) : 0);
  const displayRound = isComplete ? tr : cr;

  const bg = isDark ? "#252525" : "#E0E0E0";
  const labelColor = isDark ? "#A0A0A0" : "#404040";

  return (
    <div className="flex items-center gap-3" data-testid="round-progress-bar">
      <div className="font-display font-black text-sm uppercase tracking-wider" style={{ color: labelColor }}>
        {isComplete ? "Done" : `Round ${displayRound}`}
      </div>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: bg }}>
        <div
          className="h-full bg-[#FF6B00] transition-all duration-300"
          style={{ width: `${pct}%` }}
          data-testid="round-progress-fill"
        />
      </div>
      <div className="font-body text-xs tabular-nums" style={{ color: labelColor }} data-testid="round-progress-label">
        {displayRound}/{tr}
      </div>
    </div>
  );
}
